import { Injectable } from '@angular/core';
import { ColumnInputType } from 'app/component-library/table/column-input-type';
import { ColumnOutputType } from 'app/component-library/table/column-output-type';
import { TableAction } from 'app/component-library/table/table-action';
import { TableColumn } from 'app/component-library/table/table-column';
import { BackendService } from 'app/core/backend.service';
import { TableActions } from 'app/shared/enums/table-actions';
import { GenerateGuidService } from 'app/shared/generate-guid/generate-guid.service';
import { DateTimeFormatterPipe } from 'app/shared/pipes/date-time-formatter.pipe';
import { LookupColumnDataType } from 'app/viewmodels/lookups/lookup-column-data-type';
import { RunnableLookupItemState } from 'app/viewmodels/runnableLookups/runnable-lookup-item-state';
import { RunnableLookupRowsRangeDeleteData } from 'app/viewmodels/runnableLookups/runnable-lookup-rows-range-delete-data';
import { RunnableLookupRowsRangeDeleteDataRow } from 'app/viewmodels/runnableLookups/runnable-lookup-rows-range-delete-data-row';
import { RunnableLookupSaveDataRow } from 'app/viewmodels/runnableLookups/runnable-lookup-save-data-row';
import { RunnableLookupColumn } from 'app/viewmodels/runnableLookups/runnableLookup-column';
import { RunnableLookupRow } from 'app/viewmodels/runnableLookups/runnableLookup-row';
import { RunnableLookupCell } from 'app/viewmodels/runnableLookups/runnableLookups-cell';
import { LookupItem } from 'app/viewmodels/static-lookups/lookup-item';
import { Unit } from 'app/viewmodels/unit';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { RunnableLookupItem } from '../../viewmodels/runnableLookups/runnable-lookup-item';

@Injectable({
  providedIn: 'root',
})
export class AdminLookupDataService {

  private unitOfMeasureItems: LookupItem[];

  constructor(
    private backendService: BackendService,
    private generateGuidService: GenerateGuidService,
  ) { }

  public updateUnitOfMeasureName(result: RunnableLookupColumn): Observable<void> {
    return this.getUnits()
      .pipe(
        map(units => {
          if (result.unitOfMeasure && units) {
            const lookupItem = units.find(x => x.key === result.unitOfMeasure.uomId);

            if (lookupItem != null) {
              result.uomName = lookupItem.value;
            }
          }
        }),
      );
  }

  public getUnits(): Observable<LookupItem[]> {
    if (this.unitOfMeasureItems === undefined) {
      return this.getUnitOfMeasureItems();
    } else {
      return of(this.unitOfMeasureItems);
    }
  }

  private getUnitOfMeasureItems(): Observable<LookupItem[]> {
    return this.backendService.getAll<Unit>(Unit).pipe(map(units => {
      if (units) {
        this.unitOfMeasureItems = new Array<LookupItem>();

        units.forEach(u => {
          this.unitOfMeasureItems.push(new LookupItem(u.uomId, u.uomName));
        });

        return this.unitOfMeasureItems;
      }
    }));
  }

  public createRunnableLookupCell(lookupColumnId: string, columnIndex: number, lookupRowId: string): RunnableLookupCell {
    const cell = new RunnableLookupCell();
    cell.lookupRowId = lookupRowId;
    cell.lookupCellId = this.generateGuidService.newGuid();
    cell.columnIndex = columnIndex;
    cell.lookupColumnId = lookupColumnId;

    return cell;
  }

  public toRunnableLookupSaveDataRow(lookupRow: RunnableLookupRow): RunnableLookupSaveDataRow {
    const result: RunnableLookupSaveDataRow = new RunnableLookupSaveDataRow();
    result.lookupRowId = lookupRow.lookupRowId;
    result.rowIndex = lookupRow.rowIndex;
    result.lookupId = lookupRow.lookupId;
    result.state = lookupRow.state;

    if (lookupRow.columns) {
      const keys = Object.keys(lookupRow.columns);

      keys.forEach(guid => {
        const cell = lookupRow.columns[guid];
        result.lookupCells.push(cell);
      });
    }

    return result;
  }

  private toRunnableLookupRowsRangeDeleteDataRow(runnableLookupRow: RunnableLookupRow): RunnableLookupRowsRangeDeleteDataRow {
    const rangeDeleteDataRow = new RunnableLookupRowsRangeDeleteDataRow();
    rangeDeleteDataRow.state = runnableLookupRow.state;
    rangeDeleteDataRow.rowIndex = runnableLookupRow.rowIndex;
    rangeDeleteDataRow.lookupRowId = runnableLookupRow.lookupRowId;

    return rangeDeleteDataRow;
  }

  public updateCellWithDefaultValue(column: RunnableLookupColumn, cell: RunnableLookupCell): void {
    // this is a workaround to keep compatibly
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions,radix
    const parsedValue = <LookupColumnDataType>parseInt(<any>column.dataType);

    switch (parsedValue) {
      case LookupColumnDataType.DateTime:
        cell.value = moment().utc().toISOString();
        break;
      case LookupColumnDataType.Number:
        cell.value = '0';
        break;
      case LookupColumnDataType.Boolean:
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        cell.value = <any>false;
        break;
    }
  }

  public setColumnInputTypeAccordingDatatype(dataType: LookupColumnDataType, userDefinedColumn: TableColumn): void {
    // this is a workaround to keep compatibly
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions,radix
    const parsedValue = <LookupColumnDataType>parseInt(<any>dataType);

    switch (parsedValue) {
      case LookupColumnDataType.DateTime:
        userDefinedColumn.formatterPipe = DateTimeFormatterPipe;
        userDefinedColumn.formatterPipeArgs = ['L'];
        userDefinedColumn.inputType = ColumnInputType.DateTime;
        break;
      case LookupColumnDataType.Number:
        userDefinedColumn.inputType = ColumnInputType.Number;
        break;
      case LookupColumnDataType.Text:
        userDefinedColumn.inputType = ColumnInputType.Text;
        break;
      case LookupColumnDataType.Boolean:
        userDefinedColumn.inputType = ColumnInputType.Checkbox;
        userDefinedColumn.outputType = ColumnOutputType.Checkbox;
        break;
    }
  }

  public createDeleteTableAction(visible: boolean, title: string): TableAction {
    const tableAction = new TableAction();
    tableAction.icon = 'trash';
    tableAction.enable = false;
    tableAction.actionId = TableActions.Delete;
    tableAction.visible = visible;
    tableAction.title = title;

    return tableAction;
  }

  public createRunnableLookupColumn(isDisplayColumn: boolean): RunnableLookupColumn {
    const column = new RunnableLookupColumn();
    column.lookupColumnId = this.generateGuidService.newGuid();
    column.isDisplayColumn = isDisplayColumn;

    return column;
  }

  public createRunnableLookupRowsRangeDeleteData(rows: RunnableLookupRow[], rowsToDelete: RunnableLookupRow[], lookupId: string): RunnableLookupRowsRangeDeleteData {
    const deleteRangeData = new RunnableLookupRowsRangeDeleteData();
    deleteRangeData.lookupId = lookupId;

    if (rows) {
      deleteRangeData.rowsToDelete = rowsToDelete.map(r => this.toRunnableLookupRowsRangeDeleteDataRow(r));
      deleteRangeData.rowsToUpdate = rows
        .filter(x => x.state !== RunnableLookupItemState.Deleted && x.state !== RunnableLookupItemState.Added)
        .map(r => this.toRunnableLookupRowsRangeDeleteDataRow(r));
    }

    return deleteRangeData;
  }

  public addUniqueElementToCollection<T>(collection: T[], item: T): void {
    const index = collection.indexOf(item);

    if (index === -1) {
      collection.push(item);
    }
  }

  public removeUniqueElementFromCollection<T>(collection: T[], item: T): void {
    const index = collection.indexOf(item);

    if (index > -1) {
      collection.splice(index, 1);
    }
  }

  public isUnchangedCollection(collection: RunnableLookupItem[]) {
    return collection && collection.length > 0 && collection.every(x => x.state === RunnableLookupItemState.Unchanged);
  }
}


